import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { APP_ROUTES } from "helpers/routeHelpers";
import { feedbackFormListing, updateFeedbackForm } from "api/api.service";
import typography from "../typography";
import FeedbackFormBuilder from "../FeedbackFormBuilder/FeedbackFormBuilder";

const toFormQuestions = (questions = []) =>
    [...questions]
        .sort((a, b) => a.order - b.order)
        .map((q) => ({
            content: q.content,
            type: q.type,
            requiredField: q.requiredField,
            answerOptions: q.answerOptions?.length
                ? q.answerOptions.map((o) => ({ content: o.content }))
                : q.type === "OPTIONS"
                ? [{ content: "" }, { content: "" }]
                : [],
        }));

export default function EditFeedbackForm() {
    const { id } = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState(null);
    const [questionsLocked, setQuestionsLocked] = useState(false);

    useEffect(() => {
        // No get-by-id endpoint for a single feedback form — fetch the (small) full list
        // and find it client-side. Forms are few (one active + a handful archived at most),
        // and this keeps the Edit page working from a direct URL, not just navigation state.
        feedbackFormListing({ limit: 100 })
            .then((res) => {
                const match = res?.data?.find((f) => String(f.id) === String(id));
                if (!match) {
                    toast.error("Feedback form not found");
                    history.push(APP_ROUTES.FEEDBACK_FORMS);
                    return;
                }
                setForm(match);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, [id]);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.FEEDBACK_FORMS);
    };

    const handleSubmit = async (values) => {
        try {
            setIsSaving(true);
            const body = {
                status: form.status,
                ...(!questionsLocked && {
                    questions: values.questions.map((q) => ({
                        content: q.content.trim(),
                        type: q.type,
                        requiredField: q.requiredField,
                        ...(q.type === "OPTIONS" && {
                            answerOptions: q.answerOptions.map((o) => ({
                                content: o.content.trim(),
                            })),
                        }),
                    })),
                }),
            };
            await updateFeedbackForm({ id, bodyData: body });
            toast.success("Feedback form updated");
            history.push(APP_ROUTES.FEEDBACK_FORMS);
        } catch (error) {
            // The backend is the source of truth on whether this form still has zero
            // submissions — a client-side pre-check would just move the same race
            // condition into the frontend instead of solving it.
            if (error?.message?.includes("already has submitted feedback")) {
                setQuestionsLocked(true);
            } else {
                console.error("Error updating feedback form:", error);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <FeedbackFormBuilder
            heading={typography.EDIT_FEEDBACK_FORM}
            backButtonHandler={backButtonHandler}
            onSubmit={handleSubmit}
            isSaving={isSaving}
            loading={loading}
            questionsLocked={questionsLocked}
            initialValues={
                form ? { questions: toFormQuestions(form.questions) } : undefined
            }
        />
    );
}
